// components/dialogs/JoinEventDialog.js
import React from 'react';
import BaseDialog from '../BaseDialog';
import { formatPlannedRoundLabel } from '../../../common/plannedRoundCount';

const formatDuration = (seconds) => {
  const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const restSeconds = totalSeconds % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}時間`);
  if (minutes > 0) parts.push(`${minutes}分`);
  if (restSeconds > 0 || parts.length === 0) parts.push(`${restSeconds}秒`);

  return parts.join('');
};

const resolveRateConfirmationLabel = (event) => {
  return Array.isArray(event?.bonus_options) && event.bonus_options.includes('timing_rate')
    ? 'ベット時に確定'
    : '受付終了時に確定';
};

const resolveResultConfirmationLabel = (event) => {
  return event?.result_confirmation_method === 'owner_confirmation'
    ? '参加者による確認なし'
    : '参加者による確認あり';
};

const formatElapsedTime = (dateValue) => {
  if (!dateValue) return '不明';

  const date = new Date(dateValue);
  const now = new Date();
  const diffMs = now - date;

  const diffH = Math.floor(diffMs / 1000 / 60 / 60);
  const diffM = Math.floor((diffMs / 1000 / 60) % 60);

  if (diffH === 0 && diffM === 0) return 'たった今';
  if (diffH === 0) return `${diffM}分前`;
  if (diffM === 0) return `${diffH}時間前`;
  return `${diffH}時間${diffM}分前`;
};

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '4px 0', borderBottom: '1px solid #eee' }}>
    <span style={{ color: '#666' }}>{label}</span>
    <span style={{ fontWeight: 'bold', textAlign: 'right' }}>{value}</span>
  </div>
);

const DetailSection = ({ title, children }) => (
  <div style={{ marginTop: '10px' }}>
    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#444', marginBottom: '2px' }}>
      {title}
    </div>
    {children}
  </div>
);

const JoinEventDialog = ({ isVisible, onClose, event, tagLabels = [], onJoin, user }) => {
  const plannedRoundLabel = formatPlannedRoundLabel(event?.planned_round_count);
  const isManualBetClose = event?.bet_close_mode === 'manual';
  const canShowJoinButton = user && (
    user.ticket === undefined
    || Number(user.ticket) >= Number(event?.allowed_entry_tickets || 0)
  );

  if (!event) {
    return null;
  }

  const handleJoin = () => {
    if (onJoin && event?.room_id) {
      onJoin(event);
    } else if (event?.room_id) {
      window.location.href = `/room?id=${event.room_id}`;
    }
    onClose();
  };
  return (
    <BaseDialog isVisible={isVisible} onClose={onClose} maxHeight="min(94vh, 640px)" maxWidth="min(94vw, 420px)" padding="10px">
      <div style={{ maxWidth: '400px' }}>
        <h2 style={{ fontSize: '17px', margin: '0 0 8px' }}>
          このルームに参加しますか？
        </h2>
        {canShowJoinButton && (
          <button
            onClick={handleJoin}
            style={{
              padding: '7px 14px',
              marginBottom: '8px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            ルームに参加する
          </button>
        )}
        <div style={{ fontSize: '15px', fontWeight: 'bold', marginBottom: '6px' }}>
          {event.name}
        </div>
        {event.description && (
          <p style={{ fontSize: '13px', lineHeight: '1.45', margin: '0 0 8px', color: '#444' }}>
            {event.description}
          </p>
        )}
        {tagLabels.length > 0 && (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '8px' }}>
            {tagLabels.map((label, index) => (
              <span
                key={index}
                style={{
                  padding: '2px 7px',
                  borderRadius: '12px',
                  backgroundColor: '#e3f2fd',
                  color: '#0d47a1',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                {label}
              </span>
            ))}
          </div>
        )}

        <div style={{ fontSize: '13px', marginBottom: '10px' }}>
          {event.completion_status && event.completion_status !== 'active' && event.ended_at && (
            <DetailRow label="終了時間" value={formatElapsedTime(event.ended_at)} />
          )}

          <DetailSection title="参加条件">
            <DetailRow label="消費参加チケット" value={`${event.allowed_entry_tickets}枚`} />
            {/* <DetailRow label="開始ルームチップ" value={`${event.entry_room_points}pt`} /> */}
            <DetailRow label="予定ラウンド数" value={plannedRoundLabel} />
            {/* <DetailRow label="ベット範囲" value={`${event.minimum_bet}〜${event.max_bet}pt`} /> */}
          </DetailSection>

          <DetailSection title="ルームルール">
            <DetailRow label="レート確定" value={resolveRateConfirmationLabel(event)} />
            <DetailRow
              label="ベット終了方式"
              value={isManualBetClose ? 'オーナー操作' : '時間指定'}
            />
            {!isManualBetClose && (
              <DetailRow label="ベット受付時間" value={formatDuration(event.bet_time)} />
            )}
            <DetailRow label="結果確認" value={resolveResultConfirmationLabel(event)} />
          </DetailSection>
        </div>
        {user == null && (
          <>
            <p style={{ fontSize: '14px', lineHeight: '1.45', margin: '0 0 10px' }}>
              ルームに参加するにはログインが必要です。
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.location.href = '/login'}
                style={{
                  padding: '7px 14px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                ログイン
              </button>
              <button
                onClick={() => window.location.href = '/register'}
                style={{
                  padding: '7px 14px',
                  backgroundColor: 'white',
                  color: '#007bff',
                  border: '1px solid #007bff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                新規登録
              </button>
            </div>
          </>
        )}
        {user && user.ticket < event.allowed_entry_tickets && (
          <p style={{ fontSize: '12px', lineHeight: '1.45', margin: '0 0 10px', color: 'red', }}>
            参加チケットが足りないためルームに参加できません。
          </p>
        )}
      </div>
    </BaseDialog>
  );
};

export default JoinEventDialog;
